<template>
    <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
        <i class="fal fa-spinner fa-spin" /> Form Data Loading...
    </div>

    <div v-else class="animated fadeIn">
        <b-form @submit.prevent="onSubmit">
            <b-row class="mt-4">
                <b-col>
                    <b-card no-body>
                        <b-card-header>
                            <span class="text-muted text-uppercase">
                                Quality Control
                            </span>
                        </b-card-header>
                        <div class="py-3 px-5">
                            <b-form-group label="Sample Type" :label-cols="2">
                                <multiselect
                                    v-model="selected_sample_type"
                                    :options="sample_type_options"
                                    :multiple="false"
                                    track-by="id"
                                    label="name"
                                    :disabled="!!sample_type_id"
                                    :class="[!$v.form_data.sample_type_id.$invalid !== null ? !$v.form_data.sample_type_id.$invalid ? 'is-valid' : 'is-invalid' : null]"
                                />
                                <b-form-invalid-feedback :state="!$v.form_data.sample_type_id.$invalid">
                                    Required
                                </b-form-invalid-feedback>
                            </b-form-group>
                            <b-form-group label="Task Type" :label-cols="2">
                                <multiselect
                                    v-model="selected_task_type"
                                    :options="task_type_options"
                                    :multiple="false"
                                    track-by="name"
                                    label="name"
                                    :disabled="!!test_task || !!prep_task"
                                    :class="[!$v.form_data.schedulable_task_type.$invalid !== null ? !$v.form_data.schedulable_task_type.$invalid ? 'is-valid' : 'is-invalid' : null]"
                                />
                                <b-form-invalid-feedback :state="!$v.form_data.schedulable_task_type.$invalid">
                                    Required
                                </b-form-invalid-feedback>
                            </b-form-group>
                            <b-form-group label="Task" :label-cols="2">
                                <CustomAutocompleteField
                                    v-model="selected_task"
                                    :collection_type="selected_task_type && selected_task_type.name ? `${selected_task_type.name.toLowerCase()}_task` : null"
                                    :placeholder="taskPlaceholder"
                                    :disabled="!!test_task || !!prep_task"
                                    :filters="taskListFilters"
                                    :state="!$v.selected_task.$invalid"
                                />
                                <b-form-invalid-feedback :state="!$v.selected_task.$invalid">
                                    Required
                                </b-form-invalid-feedback>
                            </b-form-group>
                            <b-form-group label="Name" :label-cols="2">
                                <b-form-input
                                    v-model="form_data.name"
                                    type="text"
                                    placeholder="Name"
                                    :state="!$v.form_data.name.$invalid"
                                />
                                <b-form-invalid-feedback>
                                    Required
                                </b-form-invalid-feedback>
                            </b-form-group>
                            <b-form-group label="Sheet Label" :label-cols="2">
                                <b-form-input
                                    v-model="form_data.sheet_label"
                                    v-b-tooltip.html
                                    type="text"
                                    title="This is the name that will appear in the spreadsheet"
                                    placeholder="Sheet Label"
                                    :state="!$v.form_data.sheet_label.$invalid"
                                />
                                <b-form-invalid-feedback>
                                    Required
                                </b-form-invalid-feedback>
                            </b-form-group>
                            <b-form-group label="Interval" :label-cols="2">
                                <b-form-input
                                    v-model="form_data.interval"
                                    v-b-tooltip.html
                                    type="number"
                                    title="How many samples in between each instance of this quality control sample"
                                    placeholder="Sample interval"
                                    :state="!$v.form_data.interval.$invalid"
                                />
                                <b-form-invalid-feedback>
                                    Required
                                </b-form-invalid-feedback>
                            </b-form-group>
                            <b-form-group label="Interval Index" :label-cols="2">
                                <b-form-input
                                    v-model="form_data.interval_index"
                                    v-b-tooltip.html
                                    type="number"
                                    title="When this sample will appear in the group (1 means this will be the first sample to appear)"
                                    placeholder="Interval Index"
                                    :state="!$v.form_data.interval_index.$invalid"
                                />
                                <b-form-invalid-feedback>
                                    Required
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </div>
                    </b-card>
                </b-col>
            </b-row>
            
            <b-row>
                <b-col>
                    <b-card no-body>
                        <b-card-header>
                            <span class="text-muted text-uppercase">
                                Reason for Update
                            </span>
                        </b-card-header>
                        
                        <b-card-body>
                            <b-form-group label="Reason for Update" :label-cols="2">
                                <b-form-input
                                type="text"
                                v-model="form_data.update_reason"
                                placeholder="Update Reason"
                                :state="!$v.form_data.update_reason.$invalid"
                            />
                        </b-form-group>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>

            <!-- validation error -->
            <b-row v-if="invalid" class="mt-4">
                <b-col>
                    <b-alert variant="danger" show>
                        <i class="fa fa-exclamation-triangle" /> Please review the form and complete all required fields.
                    </b-alert>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <b-button
                        class="my-4 float-right py-3 px-5"
                        variant="success"
                        @click="submitForm"
                    >
                        <i class="fal fa-save" />  Save
                    </b-button>
                </b-col>
            </b-row>
        </b-form>
    </div>
</template>
<script>
import Multiselect from "vue-multiselect";
import { getCollection } from "@/shared/fetch-utils";
import { formatModelPath, stripTaskFromName } from "@/shared/utils";
import CustomAutocompleteField from '@/components/shared/CustomAutocompleteField'
import { required } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate/src'
import { requiredIf } from "vuelidate/lib/validators";

export default {
    name: "QualityControlAddForm",
    components: {
        Multiselect,
        CustomAutocompleteField
    },
    mixins: [validationMixin],
    props: {
        qualityControl: {
            type: Object,
            default: () => null
        },
        sample_type_id: {
            type: String,
            default: null
        },
        test_task: {
            type: Object,
            default: () => null
        },
        prep_task: {
            type: Object,
            default: () => null
        }
    },
    data() {
        return {
            loading: true,

            sample_type_collection: null,
            sample_type_options: [],
            task_type_options: [{name: "Prep"}, {name: "Test"}],

            selected_task: null,

            form_data: {
                name: null,
                sheet_label: null,
                interval: null,
                interval_index: null,
                schedulable_task_id: null,
                schedulable_task_type: null,
                sample_type_id: null,
                laboratory_id: !this.qualityControl && this.$store.getters.getLaboratoryContext.id,
                update_reason: null
            }
        };
    },
    validations: {
        form_data: {
            name: { required },
            sheet_label: { required },
            interval: { required },
            interval_index: { required },
            schedulable_task_type: { required },
            sample_type_id: { required },
            update_reason: {
                required: requiredIf(function () {
                  return this.qualityControl && this.qualityControl.id
                })
            }
        },

        selected_task: { required }
    },
    computed: {
        invalid() {
            return this.$v && this.$v.$dirty && this.$v.$invalid
        },

        selected_sample_type: {
            get() {
                return this.sample_type_options.find((e) => e.id === this.form_data.sample_type_id)
            },
            set(value) {
                this.form_data.sample_type_id = value && value.id
            },
        },
        selected_task_type: {
            get() {
                return this.form_data.schedulable_task_type ? { name: stripTaskFromName(formatModelPath(this.form_data.schedulable_task_type)) } : null
            },
            set(value) {
                this.form_data.schedulable_task_type = value ? `App\\Models\\${value.name}Task` : null

                this.form_data.schedulable_task_id = null
            }
        },
        taskPlaceholder() {
            let placeholderText = "Select a Task Type";

            if (this.selected_task_type) {
                placeholderText = "Select Option";
            }

            return placeholderText;
        },
        taskListFilters () {
            if (this.selected_sample_type && this.selected_task_type) {
                return [
                    {
                        logic: 'filterEquals',
                        field: `${this.selected_task_type.name.toLowerCase()}_process.sample_type_id`,
                        value: this.selected_sample_type.id
                    }
                ]
            }

            return []
        }
    },
    mounted () {
        this.loading = true
        this.init()
    },
    methods: {
        async getField(collection_name, filterDetails) {
            const collection = await getCollection({
                collection_type: collection_name,
                filters: filterDetails
            });

            return collection;
        },
        async initFormData() {
            _.keys(this.form_data).forEach((key) => {
                if (this.qualityControl[key] !== undefined && key !== 'update_reason') {
                    this.form_data[key] = this.qualityControl[key]
                }
            })

            this.selected_task = this.qualityControl.schedulable_task
        },
        async init() {
            this.sample_type_collection = await getCollection({
                collection_type: "sample_type",
                message: this.$bvToast,
            });

            this.sample_type_options = this.sample_type_collection.models.map(
                ({ id, name }) => {
                    return {
                        id,
                        name,
                    };
                }
            );

            if (this.qualityControl) {
                await this.initFormData();
            }

            if (this.sample_type_id) {
                this.selected_sample_type = this.sample_type_options.find(({ id }) => id === this.sample_type_id)
            }

            if (this.test_task) {
                this.selected_task_type = { name: 'Test' }
                this.selected_task = this.test_task
            } else if (this.prep_task) {
                this.selected_task_type = { name: 'Prep' }
                this.selected_task = this.prep_task
            }

            this.loading = false
        },
        submitForm() {
            this.$v.$touch()
            if (this.invalid) return

            if (this.selected_task) {
                this.form_data.schedulable_task_id = this.selected_task.id
            }

            return this.$emit("submit", this.form_data);
        }
    },
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
